import graphqlClient from './graphqlClient';
import { fetchAllRetailers, fetchAllChannels } from './mapPageService';
import { fetchStates } from './geographicalService';

// ── Types ─────────────────────────────────────────────────────────
export interface SiteMetrics {
  serviceableSites: number;
  acquiredCustomerSites: number;
  acquiredInPeriod: number;
  acquiredInPrevPeriod: number;
  newSiteOpeningsInPeriod: number;
  newSiteOpeningsInPrevPeriod: number;
  purchek: { installed: number; notInstalled: number };
  containment: { installed: number; notInstalled: number };
  cartManager: { installed: number; notInstalled: number };
  subscription: { installed: number; notInstalled: number };
}

export interface RetailerPenetrationRow {
  name: string; totalSites: number; acquired: number; notAcquired: number; pct: number;
}

export interface ChannelPenetrationRow {
  channel: string; customer: number; nonCustomer: number;
}

export interface SolutionRow {
  name: string; installed: number; notInstalled: number; pct: number; fill: string;
}

export interface GeoPenetrationRow {
  state: string; stateCode: string; totalSites: number; acquired: number; notAcquired: number; pct: number;
}

// Raw poi record for cross-filtering
export interface RawPoi {
  brand_name: string | null;
  channel: string | null;
  customer_id: string | null;
  zipcode: string | null;
  purchek_install_date: string | null;
  containment_install_date: string | null;
  cart_manager_install_date: string | null;
  subscription_start_date: string | null;
}

// Brand → ultimate_parent mapping
export interface BrandMapping {
  name: string;
  ultimate_parent: string;
}

export interface AllPenetrationData {
  metrics: SiteMetrics;
  rawPois: RawPoi[];
  brandMap: Map<string, string>; // brand_name → ultimate_parent
  zipToState: Map<string, { state: string; stateCode: string }>;
  allRetailers: string[];
  allChannels: string[];
  allStates: string[];
}

interface AggCount { aggregate: { count: number } }
const cnt = (a?: AggCount) => a?.aggregate?.count ?? 0;

const CACHE_KEY = 'penetration-cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getPeriodDates() {
  const today = new Date();
  const periodStart     = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()).toISOString().split('T')[0];
  const prevPeriodStart = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate()).toISOString().split('T')[0];
  return { periodStart, prevPeriodStart };
}

// ── Metrics (aggregate counts — unaffected by filters) ────────────
async function fetchMetrics(): Promise<SiteMetrics> {
  const { periodStart: ps, prevPeriodStart: pps } = getPeriodDates();

  const res = await graphqlClient.request<Record<string, AggCount>>(`
    query PenetrationMetrics {
      total:            udm_poi_aggregate { aggregate { count } }
      acquired:         udm_poi_aggregate(where: { customer_id: { _is_null: false } }) { aggregate { count } }
      acq_period:       udm_poi_aggregate(where: { _and: [{ customer_id: { _is_null: false } }, { purchek_install_date: { _gte: "${ps}" } }] }) { aggregate { count } }
      acq_prev:         udm_poi_aggregate(where: { _and: [{ customer_id: { _is_null: false } }, { purchek_install_date: { _gte: "${pps}", _lt: "${ps}" } }] }) { aggregate { count } }
      new_open_period:  udm_poi_aggregate(where: { _and: [{ customer_id: { _is_null: true } }, { opened_on: { _gte: "${ps}" } }] }) { aggregate { count } }
      new_open_prev:    udm_poi_aggregate(where: { _and: [{ customer_id: { _is_null: true } }, { opened_on: { _gte: "${pps}", _lt: "${ps}" } }] }) { aggregate { count } }
      purchek_inst:     udm_poi_aggregate(where: { purchek_install_date: { _is_null: false } }) { aggregate { count } }
      containment_inst: udm_poi_aggregate(where: { containment_install_date: { _is_null: false } }) { aggregate { count } }
      cartmgr_inst:     udm_poi_aggregate(where: { cart_manager_install_date: { _is_null: false } }) { aggregate { count } }
      sub_inst:         udm_poi_aggregate(where: { subscription_start_date: { _is_null: false } }) { aggregate { count } }
    }
  `);

  const total           = cnt(res.total);
  const purchekInst     = cnt(res.purchek_inst);
  const containmentInst = cnt(res.containment_inst);
  const cartMgrInst     = cnt(res.cartmgr_inst);
  const subInst         = cnt(res.sub_inst);

  return {
    serviceableSites:            total,
    acquiredCustomerSites:       cnt(res.acquired),
    acquiredInPeriod:            cnt(res.acq_period),
    acquiredInPrevPeriod:        cnt(res.acq_prev),
    newSiteOpeningsInPeriod:     cnt(res.new_open_period),
    newSiteOpeningsInPrevPeriod: cnt(res.new_open_prev),
    purchek:      { installed: purchekInst,     notInstalled: total - purchekInst },
    containment:  { installed: containmentInst, notInstalled: total - containmentInst },
    cartManager:  { installed: cartMgrInst,     notInstalled: total - cartMgrInst },
    subscription: { installed: subInst,         notInstalled: total - subInst },
  };
}

// ── Raw POIs (lightweight: 7 small fields, no JOINs) ──────────────
async function fetchRawPois(): Promise<RawPoi[]> {
  const res = await graphqlClient.request<{ udm_poi: RawPoi[] }>(`
    query PenetrationPois {
      udm_poi {
        brand_name
        channel
        customer_id
        zipcode
        purchek_install_date
        containment_install_date
        cart_manager_install_date
        subscription_start_date
      }
    }
  `);
  return res.udm_poi || [];
}

// ── Brand → ultimate_parent mapping ───────────────────────────────
async function fetchBrandMapping(): Promise<Map<string, string>> {
  const res = await graphqlClient.request<{
    udm_brand: Array<{ name: string; ultimate_parent: string }>;
  }>(`
    query BrandMapping {
      udm_brand(where: { ultimate_parent: { _is_null: false, _neq: "" } }) {
        name
        ultimate_parent
      }
    }
  `);

  const map = new Map<string, string>();
  for (const b of res.udm_brand || []) {
    map.set(b.name, b.ultimate_parent);
  }
  return map;
}

// ── Geo lookup (zipcode → state) ──────────────────────────────────
async function fetchGeoLookup(): Promise<Map<string, { state: string; stateCode: string }>> {
  const res = await graphqlClient.request<{
    udm_geo: Array<{ zipcode: string; state: string; state_code: string }>;
  }>(`
    query GeoLookup {
      udm_geo(where: { state: { _is_null: false, _neq: "" } }) {
        zipcode
        state
        state_code
      }
    }
  `);

  const map = new Map<string, { state: string; stateCode: string }>();
  for (const g of res.udm_geo || []) {
    map.set(g.zipcode, { state: g.state, stateCode: g.state_code || '' });
  }
  return map;
}

// ── Aggregation helpers (used by the page component) ──────────────

export function aggregateRetailers(
  pois: RawPoi[], brandMap: Map<string, string>
): RetailerPenetrationRow[] {
  const retailerMap = new Map<string, { total: number; acquired: number }>();

  for (const poi of pois) {
    const retailer = poi.brand_name ? brandMap.get(poi.brand_name) : null;
    if (!retailer) continue;
    if (!retailerMap.has(retailer)) retailerMap.set(retailer, { total: 0, acquired: 0 });
    const re = retailerMap.get(retailer)!;
    re.total++;
    if (poi.customer_id) re.acquired++;
  }

  return Array.from(retailerMap.entries())
    .map(([name, { total, acquired }]) => ({
      name, totalSites: total, acquired, notAcquired: total - acquired,
      pct: total > 0 ? (acquired / total) * 100 : 0,
    }))
    .filter(r => r.acquired > 0)
    .sort((a, b) => b.totalSites - a.totalSites);
}

export function aggregateChannels(pois: RawPoi[]): ChannelPenetrationRow[] {
  const channelMap = new Map<string, { customer: number; nonCustomer: number }>();

  for (const poi of pois) {
    if (!poi.channel) continue;
    if (!channelMap.has(poi.channel)) channelMap.set(poi.channel, { customer: 0, nonCustomer: 0 });
    const ce = channelMap.get(poi.channel)!;
    if (poi.customer_id) ce.customer++; else ce.nonCustomer++;
  }

  return Array.from(channelMap.entries())
    .map(([channel, { customer, nonCustomer }]) => ({ channel, customer, nonCustomer }))
    .filter(r => r.customer + r.nonCustomer > 0)
    .sort((a, b) => (b.customer + b.nonCustomer) - (a.customer + a.nonCustomer));
}

export function aggregateSolutions(pois: RawPoi[]): SolutionRow[] {
  const total = pois.length;
  let purchek = 0, containment = 0, cartMgr = 0, sub = 0;
  for (const poi of pois) {
    if (poi.purchek_install_date) purchek++;
    if (poi.containment_install_date) containment++;
    if (poi.cart_manager_install_date) cartMgr++;
    if (poi.subscription_start_date) sub++;
  }
  return [
    { name: 'Purchek',      installed: purchek,      notInstalled: total - purchek,      pct: total > 0 ? (purchek / total) * 100 : 0,      fill: '#003da6' },
    { name: 'Containment',  installed: containment,  notInstalled: total - containment,  pct: total > 0 ? (containment / total) * 100 : 0,  fill: '#ea580c' },
    { name: 'Cart Manager', installed: cartMgr,      notInstalled: total - cartMgr,      pct: total > 0 ? (cartMgr / total) * 100 : 0,      fill: '#16a34a' },
    { name: 'Subscription', installed: sub,           notInstalled: total - sub,           pct: total > 0 ? (sub / total) * 100 : 0,           fill: '#7c3aed' },
  ];
}

export function aggregateGeo(
  pois: RawPoi[], zipToState: Map<string, { state: string; stateCode: string }>
): GeoPenetrationRow[] {
  const stateMap = new Map<string, { stateCode: string; customer: number; nonCustomer: number }>();

  for (const poi of pois) {
    const geo = poi.zipcode ? zipToState.get(poi.zipcode) : null;
    if (!geo) continue;
    if (!stateMap.has(geo.state)) stateMap.set(geo.state, { stateCode: geo.stateCode, customer: 0, nonCustomer: 0 });
    const se = stateMap.get(geo.state)!;
    if (poi.customer_id) se.customer++; else se.nonCustomer++;
  }

  return Array.from(stateMap.entries())
    .map(([state, { stateCode, customer, nonCustomer }]) => {
      const tot = customer + nonCustomer;
      const displayState = state.charAt(0).toUpperCase() + state.slice(1);
      return { state: displayState, stateCode: stateCode || displayState, totalSites: tot, acquired: customer, notAcquired: nonCustomer, pct: tot > 0 ? (customer / tot) * 100 : 0 };
    })
    .filter(r => r.totalSites > 0)
    .sort((a, b) => b.totalSites - a.totalSites);
}

// ── Brand-level summary rows (uses existing rawPois, no new query) ─
export interface BrandSummaryRow {
  brandName: string;
  totalStores: number;
  acquiredStores: number;
  penetrationRate: number;
  totalInstallCount: number;
}

export function aggregateBrandSummary(pois: RawPoi[]): BrandSummaryRow[] {
  const map = new Map<string, { total: number; acquired: number; installs: number }>();

  for (const poi of pois) {
    const brand = poi.brand_name || 'Unknown';
    if (!map.has(brand)) map.set(brand, { total: 0, acquired: 0, installs: 0 });
    const e = map.get(brand)!;
    e.total++;
    if (poi.customer_id) e.acquired++;
    let ic = 0;
    if (poi.purchek_install_date) ic++;
    if (poi.containment_install_date) ic++;
    if (poi.cart_manager_install_date) ic++;
    if (poi.subscription_start_date) ic++;
    e.installs += ic;
  }

  return Array.from(map.entries())
    .map(([brandName, { total, acquired, installs }]) => ({
      brandName,
      totalStores: total,
      acquiredStores: acquired,
      penetrationRate: total > 0 ? (acquired / total) * 100 : 0,
      totalInstallCount: installs,
    }))
    .sort((a, b) => b.totalStores - a.totalStores);
}

// Cross-filter: apply all three filters to the raw poi array
export function filterPois(
  pois: RawPoi[],
  brandMap: Map<string, string>,
  zipToState: Map<string, { state: string; stateCode: string }>,
  selectedRetailers: string[],
  selectedChannels: string[],
  selectedStates: string[],
): RawPoi[] {
  if (selectedRetailers.length === 0 && selectedChannels.length === 0 && selectedStates.length === 0) {
    return pois;
  }

  const retailerSet = selectedRetailers.length > 0 ? new Set(selectedRetailers) : null;
  const channelSet  = selectedChannels.length > 0 ? new Set(selectedChannels) : null;
  const stateSet    = selectedStates.length > 0 ? new Set(selectedStates.map(s => s.toLowerCase())) : null;

  return pois.filter(poi => {
    if (retailerSet) {
      const retailer = poi.brand_name ? brandMap.get(poi.brand_name) : null;
      if (!retailer || !retailerSet.has(retailer)) return false;
    }
    if (channelSet) {
      if (!poi.channel || !channelSet.has(poi.channel)) return false;
    }
    if (stateSet) {
      const geo = poi.zipcode ? zipToState.get(poi.zipcode) : null;
      if (!geo || !stateSet.has(geo.state.toLowerCase())) return false;
    }
    return true;
  });
}

// ── Main fetch ─────────────────────────────────────────────────────
export const fetchAllPenetrationData = async (): Promise<AllPenetrationData> => {
  // Check session cache
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, ts } = JSON.parse(cached);
      if (Date.now() - ts < CACHE_TTL_MS) {
        console.log('[penetration] returning cached data');
        // Restore Maps from serialized arrays
        data.brandMap = new Map(data.brandMap);
        data.zipToState = new Map(data.zipToState);
        return data;
      }
    }
  } catch { /* ignore parse errors */ }

  const [rawRetailers, rawChannels, rawStates, metrics, rawPois, brandMap, zipToState] = await Promise.all([
    fetchAllRetailers(),
    fetchAllChannels(),
    fetchStates(),
    fetchMetrics(),
    fetchRawPois(),
    fetchBrandMapping(),
    fetchGeoLookup(),
  ]);

  const result: AllPenetrationData = {
    metrics, rawPois, brandMap, zipToState,
    allRetailers: rawRetailers, allChannels: rawChannels, allStates: rawStates,
  };

  // Store in session cache (serialize Maps as arrays)
  try {
    const serializable = {
      ...result,
      brandMap: Array.from(brandMap.entries()),
      zipToState: Array.from(zipToState.entries()),
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: serializable, ts: Date.now() }));
  } catch { /* storage full — ignore */ }

  console.log(`[penetration] pois:${rawPois.length} brands:${brandMap.size} zips:${zipToState.size}`);
  return result;
};
